import React, { useState, useEffect } from 'react';
import pb from '../lib/pocketbase';
import { FaFileInvoice, FaDownload, FaEye, FaCheckCircle, FaTimesCircle, FaSpinner, FaCreditCard } from 'react-icons/fa';
import { format } from 'date-fns';
import { getAuthToken } from '../components/utils/pocketbaseApi';
import { useCompany } from '../context/CompanyContext';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const Accounting = () => {
  const [invoices, setInvoices] = useState([]);
  const [upcomingInvoice, setUpcomingInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [showPaymentMethodModal, setShowPaymentMethodModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const { currentCompanyId } = useCompany();

  useEffect(() => {
    fetchInvoices();
    fetchUpcomingInvoice();
  }, [currentCompanyId]);

  const fetchUpcomingInvoice = async () => {
    try {
      const pbAuth = JSON.parse(localStorage.getItem('pocketbase_auth') || '{}');
      const companyId = currentCompanyId || pbAuth?.record?.CompanyID;

      if (!companyId) {
        throw new Error('Company ID not found');
      }

      const customer = await pb.collection('Customers').getOne(companyId);
      
      if (!customer.stripeCustId) {
        throw new Error('Stripe customer ID not found');
      }

      const token = getAuthToken();
      const response = await fetch(`${API_BASE_URL}/api/stripe/get-upcoming-invoice`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify({
          customerId: customer.stripeCustId,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch upcoming invoice');
      }

      const data = await response.json();
      setUpcomingInvoice(data.upcoming_invoice);
    } catch (err) {
      console.error('Error fetching upcoming invoice:', err);
      // Don't set error state here as it's not critical
    }
  };

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const pbAuth = JSON.parse(localStorage.getItem('pocketbase_auth') || '{}');
      const companyId = currentCompanyId || pbAuth?.record?.CompanyID;

      if (!companyId) {
        throw new Error('Company ID not found');
      }

      // Get customer data to get Stripe customer ID
      const customer = await pb.collection('Customers').getOne(companyId);
      
      if (!customer.stripeCustId) {
        throw new Error('Stripe customer ID not found');
      }

      const token = getAuthToken();
      // Fetch invoices from backend
      const response = await fetch(`${API_BASE_URL}/api/stripe/get-invoices`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify({
          customerId: customer.stripeCustId,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch invoices');
      }

      const data = await response.json();
      setInvoices(data.invoices);
      setError(null);
    } catch (err) {
      console.error('Error fetching invoices:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'open':
        return 'bg-blue-100 text-blue-800';
      case 'void':
        return 'bg-red-100 text-red-800';
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount / 100); // Convert cents to dollars
  };

  const formatDate = (timestamp) => {
    return format(new Date(timestamp * 1000), 'MMM dd, yyyy');
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([fetchInvoices(), fetchUpcomingInvoice()]);
    } finally {
      setIsRefreshing(false);
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      const pbAuth = JSON.parse(localStorage.getItem('pocketbase_auth') || '{}');
      const companyId = currentCompanyId || pbAuth?.record?.CompanyID;

      if (!companyId) {
        throw new Error('Company ID not found');
      }

      const customer = await pb.collection('Customers').getOne(companyId);
      
      if (!customer.stripeCustId) {
        throw new Error('Stripe customer ID not found');
      }

      const token = getAuthToken();
      const response = await fetch(`${API_BASE_URL}/api/stripe/list-payment-methods`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify({
          customerId: customer.stripeCustId,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch payment methods');
      }

      const data = await response.json();
      // Filter only active payment methods
      const activeMethods = data.paymentMethods?.filter(pm => pm.last4) || [];
      setPaymentMethods(activeMethods);
    } catch (err) {
      console.error('Error fetching payment methods:', err);
    }
  };

  const handlePayNowClick = () => {
    fetchPaymentMethods();
    setShowPaymentMethodModal(true);
  };

  const handleSelectPaymentMethod = (method) => {
    setSelectedPaymentMethod(method);
  };

  const handleProceedPayment = () => {
    if (selectedPaymentMethod) {
      setShowPaymentMethodModal(false);
      setShowConfirmModal(true);
    }
  };

  const handleConfirmPayment = async () => {
    try {
      setProcessingPayment(true);
      const pbAuth = JSON.parse(localStorage.getItem('pocketbase_auth') || '{}');
      const companyId = currentCompanyId || pbAuth?.record?.CompanyID;

      if (!companyId) {
        throw new Error('Company ID not found');
      }

      const customer = await pb.collection('Customers').getOne(companyId);
      
      if (!customer.stripeCustId) {
        throw new Error('Stripe customer ID not found');
      }

      const token = getAuthToken();
      const response = await fetch(`${API_BASE_URL}/api/stripe/pay-upcoming-invoice`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify({
          customerId: customer.stripeCustId,
          paymentMethodId: selectedPaymentMethod.id,
          invoiceId: selectedInvoice.id,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to process payment');
      }

      // Refresh invoices after successful payment
      await fetchInvoices();
      await fetchUpcomingInvoice();
      setShowConfirmModal(false);
      setShowInvoiceModal(false);
      setSelectedPaymentMethod(null);
      
      // Show success message
      setSuccessMessage('Payment has been made successfully!');
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 5000);
    } catch (err) {
      console.error('Payment error:', err);
      setError(err.message);
    } finally {
      setProcessingPayment(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-1/4"></div>
            <div className="h-64 bg-gray-200 rounded"></div>
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-[#0a1744]">Invoices</h1>
          <div className="flex items-center gap-4">
            <button
              onClick={handleRefresh}
              className="px-4 py-2 text-[#0a1744] border-2 border-[#0a1744] rounded-lg hover:bg-[#0a1744] hover:text-white transition-colors flex items-center gap-2"
            >
              {isRefreshing ? (
                <FaSpinner className="animate-spin" />
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              )}
              Refresh
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {successMessage && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
            {successMessage}
          </div>
        )}

        {/* Upcoming Invoice Card */}
        {upcomingInvoice && (
          <div className="bg-white rounded-lg shadow-lg p-6 mb-6 border-2 border-[#0a1744]">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h2 className="text-xl font-semibold text-[#0a1744] mb-2">Upcoming Invoice</h2>
                <p className="text-sm text-gray-600">
                  Due: {formatDate(upcomingInvoice.due_date)}
                </p>
              </div>
              <button
                onClick={() => {
                  setSelectedInvoice(upcomingInvoice);
                  setShowInvoiceModal(true);
                }}
                className="px-4 py-2 text-[#0a1744] border-2 border-[#0a1744] rounded-lg hover:bg-[#0a1744] hover:text-white transition-colors flex items-center gap-2"
              >
                <FaEye />
                View Details
              </button>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-gray-500">Amount Due</p>
                <p className="text-lg font-semibold text-[#0a1744]">
                  {formatCurrency(upcomingInvoice.amount_due)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Status</p>
                <span className={`px-2 py-1 text-sm font-semibold rounded-full ${getStatusColor(upcomingInvoice.status)}`}>
                  {upcomingInvoice.status}
                </span>
              </div>
              <div>
                <p className="text-sm text-gray-500">Next Billing Date</p>
                <p className="text-sm font-medium text-gray-900">
                  {formatDate(upcomingInvoice.subscription?.current_period_end)}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Invoices List */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Invoice
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {invoices.map((invoice) => (
                  <tr key={invoice.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <FaFileInvoice className="h-5 w-5 text-[#0a1744] mr-2" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {invoice.number}
                          </div>
                          <div className="text-sm text-gray-500">
                            {invoice.customer_name}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatDate(invoice.created)}
                      </div>
                      {/* <div className="text-sm text-gray-500">
                        Due: {formatDate(invoice.due_date)}
                      </div> */}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {formatCurrency(invoice.amount_due)}
                      </div>
                      <div className="text-sm text-gray-500">
                        Paid: {formatCurrency(invoice.amount_paid)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(invoice.status)}`}>
                        {invoice.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setSelectedInvoice(invoice);
                            setShowInvoiceModal(true);
                          }}
                          className="text-[#0a1744] hover:text-[#A1E744] transition-colors"
                          title="View Invoice"
                        >
                          <FaEye className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => window.open(invoice.invoice_pdf, '_blank')}
                          className="text-[#0a1744] hover:text-[#A1E744] transition-colors"
                          title="Download Invoice"
                        >
                          <FaDownload className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Invoice Details Modal */}
        {showInvoiceModal && selectedInvoice && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full p-6 relative">
              <button
                onClick={() => setShowInvoiceModal(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              <div className="mb-6">
                <h2 className="text-2xl font-bold text-[#0a1744] mb-2">
                  {selectedInvoice === upcomingInvoice ? 'Upcoming Invoice' : `Invoice #${selectedInvoice.number}`}
                </h2>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 text-sm font-semibold rounded-full ${getStatusColor(selectedInvoice.status)}`}>
                    {selectedInvoice.status}
                  </span>
                  {selectedInvoice.status === 'paid' && (
                    <FaCheckCircle className="text-green-500" />
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-6 mb-6">
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Bill To</h3>
                  <div className="text-sm text-gray-900">
                    {selectedInvoice.billed_to ? (
                      <>
                        <p className="font-medium">{selectedInvoice.billed_to.name}</p>
                        <p>{selectedInvoice.billed_to.email}</p>
                        {selectedInvoice.billed_to.phone && <p>{selectedInvoice.billed_to.phone}</p>}
                        {selectedInvoice.billed_to.address && (
                          <p className="text-gray-500 mt-1 whitespace-pre-line">
                            {typeof selectedInvoice.billed_to.address === 'object' 
                              ? Object.values(selectedInvoice.billed_to.address).filter(Boolean).join(', ') 
                              : selectedInvoice.billed_to.address}
                          </p>
                        )}
                      </>
                    ) : (
                      <>
                        <p className="font-medium">{selectedInvoice.customer_name}</p>
                        <p>{selectedInvoice.customer_email}</p>
                      </>
                    )}
                  </div>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Invoice Details</h3>
                  <div className="text-sm text-gray-900">
                    <p>Date: {formatDate(selectedInvoice.created)}</p>
                    {/* <p>Due Date: {formatDate(selectedInvoice.due_date)}</p> */}
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-sm font-medium text-gray-500 mb-4">Items</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead>
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Quantity</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {selectedInvoice?.line_items?.map((item, index) => (
                        <tr key={index}>
                          <td className="px-4 py-2 text-sm text-gray-900">
                            {item.productName || item.description}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-900 text-right">{item.quantity}</td>
                          <td className="px-4 py-2 text-sm text-gray-900 text-right">
                            {formatCurrency(item.amount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td className="px-4 py-2 text-sm font-medium text-gray-900 text-left">
                          {selectedInvoice?.productName}
                        </td>
                        <td className="px-4 py-2 text-sm font-medium text-gray-900 text-right">
                          Total
                        </td>
                        <td className="px-4 py-2 text-sm font-medium text-gray-900 text-right">
                          {formatCurrency(selectedInvoice.amount_due)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              {selectedInvoice.payment_intent && (
                <div className="mt-6 border-t border-gray-200 pt-6">
                  <h3 className="text-sm font-medium text-gray-500 mb-4">Payment Information</h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-500">Payment Status</p>
                        <p className="text-sm font-medium text-gray-900">
                          {selectedInvoice.payment_intent.status}
                        </p>
                      </div>
                      {selectedInvoice.payment_intent.payment_method?.card && (
                        <div>
                          <p className="text-sm text-gray-500">Payment Method</p>
                          <p className="text-sm font-medium text-gray-900">
                            {selectedInvoice.payment_intent.payment_method.card.brand.toUpperCase()} ending in{' '}
                            {selectedInvoice.payment_intent.payment_method.card.last4}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-6 flex justify-end gap-4">
                {selectedInvoice.status !== 'paid' && (
                  <button
                    onClick={handlePayNowClick}
                    disabled={processingPayment}
                    className="px-4 py-2 bg-[#0a1744] text-white rounded-lg hover:bg-[#A1E744] hover:text-[#0a1744] transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {processingPayment ? (
                      <>
                        <FaSpinner className="animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <FaCreditCard />
                        Pay Now
                      </>
                    )}
                  </button>
                )}
                {selectedInvoice.hosted_invoice_url && (
                  <button
                    onClick={() => window.open(selectedInvoice.hosted_invoice_url, '_blank')}
                    className="px-4 py-2 bg-[#A1E744] text-[#0a1744] border-2 border-[#A1E744] rounded-lg hover:bg-white transition-colors flex items-center gap-2 font-medium"
                  >
                    <FaFileInvoice />
                    View your Payment receipt
                  </button>
                )}
                {selectedInvoice.invoice_pdf && (
                  <button
                    onClick={() => window.open(selectedInvoice.invoice_pdf, '_blank')}
                    className="px-4 py-2 text-[#0a1744] border-2 border-[#0a1744] rounded-lg hover:bg-[#0a1744] hover:text-white transition-colors flex items-center gap-2"
                  >
                    <FaDownload />
                    View your Invoice
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Payment Method Selection Modal */}
        {showPaymentMethodModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 relative">
              <button
                onClick={() => {
                  setShowPaymentMethodModal(false);
                  setSelectedPaymentMethod(null);
                }}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              <h2 className="text-xl font-bold text-[#0a1744] mb-4">
                Select Payment Method
              </h2>

              <div className="space-y-3 mb-6">
                {paymentMethods.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">No active payment methods found</p>
                ) : (
                  paymentMethods.map((method) => (
                    <div
                      key={method.id}
                      onClick={() => handleSelectPaymentMethod(method)}
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                        selectedPaymentMethod?.id === method.id
                          ? 'border-[#0a1744] bg-gray-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <FaCreditCard className="text-[#0a1744]" />
                          <div>
                            <p className="font-medium text-gray-900">
                              {method.brand?.toUpperCase()} ending in {method?.last4}
                            </p>
                            <p className="text-sm text-gray-500">
                              Expires {method?.expMonth}/{method?.expYear}
                            </p>
                          </div>
                        </div>
                        {selectedPaymentMethod?.id === method.id && (
                          <FaCheckCircle className="text-green-500" />
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowPaymentMethodModal(false);
                    setSelectedPaymentMethod(null);
                  }}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleProceedPayment}
                  disabled={!selectedPaymentMethod}
                  className="px-4 py-2 bg-[#0a1744] text-white rounded-lg hover:bg-[#A1E744] hover:text-[#0a1744] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Proceed
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Confirmation Warning Modal */}
        {showConfirmModal && selectedPaymentMethod && selectedInvoice && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 relative">
              <div className="flex items-center justify-center mb-4">
                <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
              </div>

              <h2 className="text-xl font-bold text-[#0a1744] text-center mb-2">
                Confirm Payment
              </h2>

              <p className="text-gray-600 text-center mb-6">
                This will attempt the payment on the selected card for <span className="font-semibold">{formatCurrency(selectedInvoice.amount_due)}</span>
              </p>

              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="flex items-center gap-3">
                  <FaCreditCard className="text-[#0a1744]" />
                  <div>
                    <p className="font-medium text-gray-900">
                      {selectedPaymentMethod?.brand?.toUpperCase()} ending in {selectedPaymentMethod?.last4}
                    </p>
                    <p className="text-sm text-gray-500">
                      Expires {selectedPaymentMethod?.expMonth}/{selectedPaymentMethod?.expYear}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowConfirmModal(false);
                    setShowPaymentMethodModal(true);
                  }}
                  disabled={processingPayment}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmPayment}
                  disabled={processingPayment}
                  className="px-4 py-2 bg-[#0a1744] text-white rounded-lg hover:bg-[#A1E744] hover:text-[#0a1744] transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {processingPayment ? (
                    <>
                      <FaSpinner className="animate-spin" />
                      Processing...
                    </>
                  ) : (
                    'Yes, Proceed'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Accounting; 